import { FormFieldCheckboxComponent } from '../../../../ui/molecules/form-field-checkbox/form-field-checkbox.component';
import { FORM_COMPONENT, VALIDATION_SCHEMA_CONFIG } from '../../../../../tokens';
import { LoggerService } from '../../../../logger/logger.service';
import { DOMService } from '../../../../services/dom.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { Link, FlashSaleFormSlice } from '@repo/shared';
import { SliceWithData } from '../../../../../typings';
import { AbstractFormComponent } from '../abstract-form-component';
import { SliceFormsService } from '../slice-forms.service';
import { Router } from '@angular/router';
import { JSONSchema7 } from 'json-schema';
import { RECAPTCHA_LANGUAGE, ReCaptchaV3Service } from 'ng-recaptcha';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Logger } from '../../../../logger/logger';
import { Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { FLASH_SALE_FORM } from '@repo/shared/form-schemas/flash-sale';
import { WINDOW } from '../../../../services/window.provider';

export const QUICK_SELLING_CONFIRMATION_PAGE_URL = 'confirmation-vente-flash-immo';

@Component({
  selector: 'slice-flash-sale-form',
  templateUrl: './slice-flash-sale.component.html',
  styleUrls: ['../slice-forms.scss', './slice-flash-sale.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: VALIDATION_SCHEMA_CONFIG,
      useValue: FLASH_SALE_FORM,
    },
    {
      provide: FORM_COMPONENT,
      useExisting: forwardRef(() => SliceFlashSaleFormComponent),
    },
    {
      provide: RECAPTCHA_LANGUAGE,
      useValue: 'fr',
    },
  ],
})
export class SliceFlashSaleFormComponent
  extends AbstractFormComponent
  implements SliceWithData<FlashSaleFormSlice>, OnInit, OnDestroy, AfterViewInit {
  @Input() data: FlashSaleFormSlice;

  unSubcribe$ = new Subject();
  logger: Logger;
  form: FormGroup;

  selectError = false;
  _buttonDisabled = false;
  _hasPostError = false;
  _hideCheckboxError = true;
  isCheckboxChecked = false;

  @ViewChild(FormFieldCheckboxComponent) formFieldCheckboxComponent: FormFieldCheckboxComponent;

  get button(): Link {
    return { label: 'Être Rappelé' };
  }

  constructor(
    @Inject(VALIDATION_SCHEMA_CONFIG) protected readonly _flashSaleFormValidationSchema: JSONSchema7,
    @Inject(WINDOW) protected readonly win: Window,
    private readonly sliceFormsService: SliceFormsService,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    readonly domService: DOMService,
    readonly elementRef: ElementRef,
    readonly loggerService: LoggerService,
    private recaptchaV3Service: ReCaptchaV3Service,
  ) {
    super(_flashSaleFormValidationSchema, elementRef, domService, changeDetectorRef);
    this.logger = loggerService.get(SliceFlashSaleFormComponent.name);
  }

  ngOnInit(): void {
    this.form = this.sliceFormsService.buildForm(this.getFormValidationSchema());

    if (this.form) {
      this.form?.patchValue({ textRadioButton: false });
      this.form?.patchValue({ emailRedirection: this.data.email_redirection });
      this.form?.patchValue({ recipt: this.data.recipt });

      this.form?.patchValue({ providerCode: this.win.sessionStorage.getItem('provider_code') || '' });
    }
  }

  ngAfterViewInit(): void {
    this.form.addControl('recaptcha', new FormControl('', Validators.required));
    this.form?.get('textadioButton')?.enable();
  }

  ngOnDestroy(): void {
    this.unSubcribe$.next(null);
    this.unSubcribe$.unsubscribe();
  }

  checkRecaptcha(): void {
    this.recaptchaV3Service.execute('flashSaleForm').subscribe(
      token => {
        this.form?.patchValue({ recaptcha: token });
        this.submitForm();
      },
      error => {
        this.logger.error('Error trying to verify request (reCaptcha v3)', error);
      },
    );
  }

  submitForm(): void {
    const { valid, value } = this.form;

    if (valid) {
      this._buttonDisabled = true;

      this.sliceFormsService
        .sendFlashSaleContactForm(value)
        .pipe(takeUntil(this.unSubcribe$))
        .subscribe(
          () => {
            sessionStorage.removeItem('provider_code');
            this.router.navigateByUrl(QUICK_SELLING_CONFIRMATION_PAGE_URL);
          },
          (error: HttpErrorResponse) => {
            this.logger.error('Could not submit form data:', error);
            this._buttonDisabled = false;
            this._hasPostError = true;
            this.changeDetectorRef.detectChanges();
          },
        );
    } else if (!valid) {
      this.triggerFormFieldsStatus(this.form);
      this.hanldeCheckBoxErrorField();
      this.changeDetectorRef.detectChanges();
      this.focusFirstInvalidFormField();
    }
  }

  getCheckBoxValue(value: boolean): void {
    this._hideCheckboxError = true;
    this.form?.patchValue({ textRadioButton: value });
  }

  hanldeCheckBoxErrorField(): void {
    const isFormValid = this.form.valid;
    const isCheckboxChecked = this.form?.get('textRadioButton')?.value;
    this._hideCheckboxError = isFormValid || isCheckboxChecked;
  }
}
